import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { validateDownloadToken, DownloadValidationResult, InvoiceAttachment } from '../services/downloadService';
import { DocumentArrowDownIcon, ExclamationTriangleIcon, CheckCircleIcon, FolderIcon, ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline';

const formatDate = (timestamp: any): string => {
  if (!timestamp) return '-';
  try {
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('hu-HU');
  } catch {
    return '-';
  }
};

const DownloadPage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const [result, setResult] = useState<DownloadValidationResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());

  useEffect(() => {
    const validateToken = async () => {
      if (!token) {
        setError('Hiányzó letöltési link');
        setLoading(false);
        return;
      }

      try {
        const data = await validateDownloadToken(token);
        if (data) {
          setResult(data);
        } else {
          setError('A letöltési link érvénytelen vagy lejárt.\nA link a következő számla kiállításáig volt érvényes.');
        }
      } catch (err) {
        setError('Hiba történt a link ellenőrzése során.');
      } finally {
        setLoading(false);
      }
    };

    validateToken();
  }, [token]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Link ellenőrzése...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center px-4">
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full text-center">
          <ExclamationTriangleIcon className="h-16 w-16 text-amber-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Link érvénytelen</h1>
          <p className="text-gray-600 whitespace-pre-line">{error}</p>
          <div className="mt-6 text-sm text-gray-500">
            <p>Ha segítségre van szüksége, kérjük vegye fel a kapcsolatot velünk:</p>
            <a href="mailto:info@aram-foldgaz.hu" className="text-green-600 hover:underline mt-1 inline-block">
              info@aram-foldgaz.hu
            </a>
          </div>
        </div>
      </div>
    );
  }

  if (!result) return null;

  return (
    <div className="min-h-screen bg-gray-100 py-8 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
          <div className="flex items-center space-x-4">
            <div className="bg-green-100 p-3 rounded-full">
              <CheckCircleIcon className="h-8 w-8 text-green-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                Ügyfél Portál
              </h1>
              <p className="text-lg text-gray-600">{result.customerName}</p>
              <p className="text-sm text-gray-500">
                {result.invoiceCount} számla elérhető
              </p>
            </div>
          </div>
        </div>

        {/* Invoice List */}
        <div className="space-y-4">
          {result.invoices.map((invoice: InvoiceAttachment) => {
            const isExpanded = expandedItems.has(invoice.id);
            return (
              <div key={invoice.id} className="bg-white rounded-lg shadow-md overflow-hidden">
                {/* Header - always visible */}
                <div className="p-5">
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between">
                    <div className="flex-1">
                      <h2 className="text-lg font-semibold text-gray-900">
                        {invoice.invoiceNumber || 'Névtelen számla'}
                      </h2>
                      <div className="text-sm text-gray-500 mt-1 flex flex-wrap gap-x-3">
                        <span>Kiállítva: {formatDate(invoice.issueDate)}</span>
                        {invoice.dueDate && (
                          <span>Fizetési határidő: {formatDate(invoice.dueDate)}</span>
                        )}
                      </div>
                      {invoice.buyerAddress && (
                        <div className="text-sm text-gray-600 mt-1">
                          {invoice.buyerAddress}
                        </div>
                      )}
                    </div>
                    <div className="mt-3 md:mt-0 text-right md:pl-6">
                      <p className="text-xl font-bold text-green-700">
                        {invoice.grossAmount.toLocaleString('hu-HU')} Ft
                      </p>
                      <p className="text-xs text-gray-500">Bruttó összeg</p>
                    </div>
                  </div>

                  {/* Action buttons - always visible */}
                  <div className="flex flex-wrap gap-2 mt-4">
                    {invoice.pdfBase64 && (
                      <a
                        href={`data:application/pdf;base64,${invoice.pdfBase64}`}
                        download={`${invoice.invoiceNumber || 'szamla'}.pdf`}
                        className="inline-flex items-center px-3 py-1.5 bg-gray-800 text-white text-sm rounded-md hover:bg-gray-900 transition-colors no-underline"
                      >
                        <DocumentArrowDownIcon className="h-4 w-4 mr-1.5" />
                        Számla PDF
                      </a>
                    )}
                    {invoice.attachmentUrl && invoice.attachmentStatusPdf !== 'invalid' && (
                      <a
                        href={invoice.attachmentUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center px-3 py-1.5 bg-red-50 text-red-700 text-sm rounded-md hover:bg-red-100 transition-colors border border-red-200 no-underline"
                      >
                        <DocumentArrowDownIcon className="h-4 w-4 mr-1.5" />
                        PDF melléklet
                      </a>
                    )}
                    {invoice.attachmentExcelUrl && invoice.attachmentStatusExcel !== 'invalid' && (
                      <a
                        href={invoice.attachmentExcelUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center px-3 py-1.5 bg-green-50 text-green-700 text-sm rounded-md hover:bg-green-100 transition-colors border border-green-200 no-underline"
                      >
                        <DocumentArrowDownIcon className="h-4 w-4 mr-1.5" />
                        Excel melléklet
                      </a>
                    )}

                    {/* Toggle items button */}
                    {invoice.items && invoice.items.length > 0 && (
                      <button
                        onClick={() => {
                          setExpandedItems(prev => {
                            const next = new Set(prev);
                            if (next.has(invoice.id)) {
                              next.delete(invoice.id);
                            } else {
                              next.add(invoice.id);
                            }
                            return next;
                          });
                        }}
                        className="inline-flex items-center px-3 py-1.5 bg-gray-100 text-gray-700 text-sm rounded-md hover:bg-gray-200 transition-colors ml-auto"
                      >
                        {isExpanded ? (
                          <>
                            <ChevronUpIcon className="h-4 w-4 mr-1.5" />
                            Tételek elrejtése
                          </>
                        ) : (
                          <>
                            <ChevronDownIcon className="h-4 w-4 mr-1.5" />
                            Tételek ({invoice.items.length})
                          </>
                        )}
                      </button>
                    )}
                  </div>
                </div>

                {/* Collapsible items section */}
                {isExpanded && invoice.items && invoice.items.length > 0 && (
                  <div className="px-5 pb-5">
                    <div className="border-t border-gray-200 pt-4">
                      <h3 className="text-sm font-medium text-gray-700 mb-3">Számla tételei</h3>
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="text-gray-500 border-b border-gray-200">
                              <th className="text-left py-2 pr-4">Megnevezés</th>
                              <th className="text-right py-2 px-4">Mennyiség</th>
                              <th className="text-right py-2 px-4">Egységár</th>
                              <th className="text-right py-2 px-4">ÁFA</th>
                            </tr>
                          </thead>
                          <tbody>
                            {invoice.items.map((item, idx) => (
                              <tr key={idx} className="border-b border-gray-100 last:border-0">
                                <td className="py-2 pr-4 text-gray-700">{item.name || '-'}</td>
                                <td className="py-2 px-4 text-right text-gray-600">
                                  {item.quantity ? Number(item.quantity).toFixed(2) : '0'} {item.unit || 'db'}
                                </td>
                                <td className="py-2 px-4 text-right text-gray-600">
                                  {item.unitPrice?.toLocaleString('hu-HU')} Ft
                                </td>
                                <td className="py-2 px-4 text-right text-gray-600">{item.taxRate}%</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {result.invoices.length === 0 && (
          <div className="bg-white rounded-lg shadow-lg p-8 text-center">
            <FolderIcon className="h-16 w-16 text-gray-300 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-700 mb-2">Nincs elérhető számla</h2>
            <p className="text-gray-500">Jelenleg nincs megjeleníthető számla vagy melléklet.</p>
          </div>
        )}

        {/* Footer */}
        <div className="mt-8 text-center text-sm text-gray-500">
          <p>Energia Info Kft. - Energia ERP</p>
          <p className="mt-1 text-xs text-gray-400">
            A link személyre szól és a következő számla kiállításáig érvényes.
          </p>
        </div>
      </div>
    </div>
  );
};

export default DownloadPage;
